import {Injectable} from '@angular/core';
import {nanoid} from 'nanoid';

type TxTask<T = unknown> = () => Promise<T>;

enum QueuedTxStatus {
    QUEUED,
    IN_PROGRESS,
    COMPLETED,
    FAILED,
    CANCELLED
}

interface QueuedTx<T = unknown> {
    id: string;
    task: TxTask<T>;
    status: QueuedTxStatus;
    createdAt: number;
    resolve: (value: T) => void;
    reject: (reason?: any) => void;
}

interface Queue {
    items: QueuedTx[];
    processing: boolean;
}

@Injectable({providedIn: 'root'})
export class DexTxQueueService {

    private readonly queues = new Map<string, Queue>();

    public enqueue<T>(address: string, task: TxTask<T>): Promise<T> {
        const id = nanoid();
        let resolve: (value: T) => void;
        let reject: (reason?: any) => void;
        const promise = new Promise<T>((res, rej) => {
            resolve = res;
            reject = rej;
        });

        const item: QueuedTx<T> = {
            id,
            task,
            status:    QueuedTxStatus.QUEUED,
            createdAt: Date.now(),
            resolve:   resolve!,
            reject:    reject!
        };

        const addressQueue = this.getOrCreateQueue(address);
        addressQueue.items.push(item as QueuedTx);

        this.processQueue(address);

        return promise;
    }

    public cancel(address: string, id: string): boolean {
        const addressQueue = this.queues.get(address);
        if (!addressQueue) return false;

        const index = addressQueue.items.findIndex(item => item.id === id);
        if (index === -1) return false;

        const [removed] = addressQueue.items.splice(index, 1);

        if (removed.status === QueuedTxStatus.QUEUED) {
            removed.status = QueuedTxStatus.CANCELLED;
            removed.reject(new Error('Transaction cancelled'));
        }

        return true;
    }

    public getQueue(address: string): QueuedTx[] {
        return this.queues.get(address)?.items.slice() ?? [];
    }

    private getOrCreateQueue(address: string) {
        let queue = this.queues.get(address);

        if (!queue) {
            queue = {items: [], processing: false};
            this.queues.set(address, queue);
        }

        return queue;
    }

    private async processQueue(address: string): Promise<void> {
        const addressQueue = this.queues.get(address);
        if (!addressQueue || addressQueue.processing) return;

        addressQueue.processing = true;
        try {
            while (addressQueue.items.length > 0) {
                const item = addressQueue.items[0];
                item.status = QueuedTxStatus.IN_PROGRESS;
                try {
                    const result = await item.task();
                    item.status = QueuedTxStatus.COMPLETED;
                    item.resolve(result);
                } catch (error) {
                    item.status = QueuedTxStatus.FAILED;
                    item.reject(error);
                } finally {
                    const index = addressQueue.items.indexOf(item);
                    if (index !== -1) addressQueue.items.splice(index, 1);
                }
            }
        } finally {
            addressQueue.processing = false;

            if (addressQueue.items.length > 0) {
                this.processQueue(address);
            }
        }
    }
}
